import React, { useEffect, useRef, useState, useContext } from "react";
import { isChildElement } from "../../utils/isChildElement";
import { ISelectedRange, ISubtitleWord } from "./SubtitlesTypes";
import { uploadJson } from "../../api/api";
import { ContextMenu } from "./SubtitlesContextMenu";
import { CommentPopup } from "./SubtitlesCommentPopup";
import './Subtitles.css';
import { ISubtitles } from "../../types/videoResponse";
import { AnalysisProvider } from "../../api/fileProvider/analysisProvider";
import { IFullAnalysisFileRecord } from "../../pages/dashboardPage/components/fullAnalysis/FullAnalysisTypes";
import { formatDate2 } from "../../utils/dateFormats";
import { AppContext } from "../../appContext";

interface ISubtitlesProps{
    subtitles: ISubtitles;
    onWordClick: (time: number)=>void;
    onSave: (selections: ISelectedRange[])=>void;
    onWordSave: (segmentIndex: number, wordIndex: number, text: string)=>void;
    ref?: React.RefObject<HTMLDivElement>;
    selectedTime: number;
    selections: Array<ISelectedRange>;
    setSelections: React.Dispatch<React.SetStateAction<ISelectedRange[]>>;
    onHoverWord: (result:{word: ISubtitleWord, element:HTMLElement})=>void;
    onSelectionAction?: (action: string, selection: ISelectedRange)=>void;
}

export const Subtitles = ({subtitles, onWordClick, ref, selectedTime, onWordSave, onSave, selections, setSelections, onHoverWord, onSelectionAction}: ISubtitlesProps)=>{
    const { translate } = useContext(AppContext);
    const [isEditFocused, setEditFocused] = useState(false);
    const [showContextMenu, setShowContextMenu] = useState<{x: number, y: number}>(null);
    const contextMenuRef = useRef<HTMLDivElement>(null);

    const [showEditComment, setShowEditComment] = useState<{x: number, y: number, comment?:ISelectedRange}>(null);
    const editCommentRef = useRef<HTMLDivElement>(null);
    const [selectionRange, setSelectionRange] = useState<Range>(null);
    const [hoverTooltip, setHoverTooltip] = useState<{x: number, y: number, text: string} | null>(null);
    const [editIndex, setEditIndex] = useState(-1);
    const [editValue, setEditValue] = useState('');

    useEffect(()=>{
        if (ref.current){
            const scrollElement = ref.current.parentElement.parentElement.parentElement;
            const activeWord = ref.current.querySelector('.subtitles_word--playing') || ref.current.querySelector('.subtitles_word--preplaying');
            if (activeWord){
                const top = (activeWord as HTMLElement).offsetTop;
                const wordHeight = (activeWord as HTMLElement).getBoundingClientRect().height || 0;
                const isBefore = (top <  scrollElement.scrollTop);
                const isAfter = (top + wordHeight > (scrollElement.scrollTop + scrollElement.clientHeight))
                const inVisible = !isBefore && !isAfter;
                if (isAfter){
                    scrollElement.scrollTo(0, top + wordHeight - scrollElement.clientHeight);
                }
                if (isBefore){
                    scrollElement.scrollTo(0, top);
                }
            }
        }
    }, [selectedTime, ref.current]);

    useEffect(()=>{
        const windowHandler = (e: MouseEvent)=>{
            if (!isChildElement(e.target as HTMLElement, contextMenuRef.current)){
                setShowContextMenu(null);
            }
        }
        window.addEventListener('click', windowHandler);
        return ()=>{
            window.removeEventListener('click', windowHandler);
        }
    }, []);

    useEffect(()=>{
        if (subtitles.selections){
            setSelections(subtitles.selections);
        }
    }, [subtitles.selections]);


    useEffect(()=>{
        if (isEditFocused == false){
            ref.current.style.userSelect = 'none';
            return;
        }
        const handleSelectionChange = (()=>{
            const selection = document.getSelection();
            if (!selection.rangeCount) return;
            setSelectionRange(selection.getRangeAt(0));
        });
        document.addEventListener('selectionchange', handleSelectionChange);
        return ()=>{
            document.removeEventListener('selectionchange', handleSelectionChange);
        }
    }, [isEditFocused]);

    let wordCount = 0;

    const getSubtitleWordStyle = (word:ISubtitleWord, lastWord: ISubtitleWord, wordIndex: number) => {
        const wordFeatures = selections.filter(it=>it.startId <= wordIndex && it.endId >= wordIndex);
        const featureStyles = wordFeatures.map(feature => {
            const featureStart = feature.startId == wordIndex ? `subtitles_word--${feature.type}-start` : '';
            return `subtitles_word--${feature.type} ${featureStart}`;
        });
        const styles = [
            'subtitles_word',
            ...featureStyles,
            (word.start <= selectedTime && word.end > selectedTime) ? 'subtitles_word--playing': '',
            (lastWord && lastWord.end <= selectedTime && word.start > selectedTime) ? 'subtitles_word--preplaying': '',
        ];
        return styles.join(' ');
    }
    let lastWord: ISubtitleWord = null;

    const getRangesInRange = (range: Range):ISelectedRange[] => {
        const startNode = range.startContainer.parentNode as HTMLElement;
        const endNode = range.endContainer.parentNode as HTMLElement;
        const startId = Number(startNode?.dataset?.['id'] || -1);
        const endId = Number(endNode?.dataset?.['id'] || -1);
        if ( startId != -1 && startId != -1){
            return selections.filter(selection=>{
                return selection.startId <= startId && selection.endId >= startId 
                || selection.startId <= endId && selection.endId >= endId
                || selection.startId >= startId && selection.startId <= endId 
                || selection.endId >= startId && selection.endId <= endId 
            });
        }
        return [];
    }

    const getSelectedRange = ()=>{
        const startNode = selectionRange.startContainer.parentNode as HTMLElement;
        const endNode = selectionRange.endContainer.parentNode as HTMLElement;
        const newSelection: ISelectedRange = {
            startId: Number(startNode.dataset['id']), 
            endId: Number(endNode.dataset['id']), 
            startOffset: selectionRange.startOffset, 
            endOffset: selectionRange.endOffset,
            type: '',
        }; 
        return newSelection;
    }

    // Extract selected text content from subtitles based on selection range
    const getSelectedText = (selection: ISelectedRange): string => {
        let wordCount = 0;
        const resultWords: string[] = [];
        subtitles.segments.forEach((segment, segmentIndex)=>{
            segment.words.forEach((word, wordIndex)=>{
                wordCount++;
                if (wordCount >= selection.startId && wordCount <= selection.endId){
                    resultWords.push(word.word);
                }
            });
        });
        return resultWords.join(' ');
    }

    // Handle Send to Desktop functionality
    const handleSendToDesktop = async () => {
        try {
            // Get current selection or use stored selection range
            let currentSelection = selectionRange;
            if (!currentSelection) {
                const selection = document.getSelection();
                if (!selection || !selection.rangeCount) {
                    showNotification(translate('subtitles_select_text_to_send'), 'error');
                    return;
                }
                currentSelection = selection.getRangeAt(0);
            }

            const selection = getSelectedRangeFromRange(currentSelection);
            const selectedText = getSelectedText(selection);
            
            if (!selectedText || selectedText.trim() === '') {
                showNotification(translate('subtitles_no_text_selected'), 'error');
                return;
            }

            // Create AnalysisProvider to handle file creation
            const provider = new AnalysisProvider();
            provider.onChange = () => {}; // Required callback for provider to function
            
            // Create analysis file record with selected text
            const newRecord: IFullAnalysisFileRecord = {
                name: `${translate('subtitles_quote_from_meeting')} - ${formatDate2(new Date())}`,
                tags: [translate('subtitles_meeting_quote_tag')],
                date: new Date().toISOString(),
                action: 'quote',
                relatedClientId: '',
                chunks: [{
                    type: 'quote',
                    data: {
                        title: translate('subtitles_selected_text_title'),
                        text: selectedText
                    }
                }]
            };

            // Create file in root desktop directory
            const newFile = {
                name: newRecord.name,
                iconIndex: 0,
                date: new Date(),
                type: 'file' as const,
                fileData: [newRecord],
                mimeType: 'text/analysis'
            };

            await provider.createFolder(newFile, null);
            console.log('File created successfully in desktop');
            showNotification(translate('subtitles_text_saved_success'), 'success');
            
        } catch (error) {
            console.error('Failed to send to desktop:', error);
            showNotification(translate('subtitles_save_failed'), 'error');
        }
    }

    // Helper function to get selection range from a Range object
    const getSelectedRangeFromRange = (range: Range): ISelectedRange => {
        const startNode = range.startContainer.parentNode as HTMLElement;
        const endNode = range.endContainer.parentNode as HTMLElement;
        return {
            startId: Number(startNode.dataset['id']), 
            endId: Number(endNode.dataset['id']), 
            startOffset: range.startOffset, 
            endOffset: range.endOffset,
            type: '',
        };
    }

    // Show notification to user
    const showNotification = (message: string, type: 'success' | 'error') => {
        const notification = document.createElement('div');
        notification.className = `subtitles_notification subtitles_notification--${type}`;
        notification.textContent = message;
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 12px 24px;
            border-radius: 6px;
            color: white;
            font-weight: 500;
            z-index: 10000;
            animation: slideIn 0.3s ease-out;
            background-color: ${type === 'success' ? '#4CAF50' : '#F44336'};
        `;
        
        document.body.appendChild(notification);
        setTimeout(() => {
            notification.style.animation = 'slideOut 0.3s ease-in';
            setTimeout(() => {
                if (document.body.contains(notification)) {
                    document.body.removeChild(notification);
                }
            }, 300);
        }, 3000);
    }

    return <div>
        <div tabIndex={0} ref={ref} className="subtitles" 
        onFocus={()=>{document.documentElement.style.userSelect = 'text'; ref.current.style.userSelect = 'text'; setEditFocused(true)}} 
        onBlur={()=>{document.documentElement.style.userSelect = ''; ref.current.style.userSelect = 'none'; setEditFocused(false)}}
        onContextMenu={(e)=>{
            e.preventDefault();
            setShowContextMenu({x: e.clientX, y: e.clientY});
            return;
        }}
        >
        {subtitles.segments.map((segment, segmentIndex)=>{
            return <div key={`segment-${segmentIndex}`} className={`subtitles_segment ${segment.speaker == 'SPEAKER_00' ? 'subtitles_segment--opponent' : ''}`}>
                {segment.words.map((word, wordIndex)=>{
                    wordCount++;
                    const result = (editIndex == wordCount) ? <span className="subtitles_word_editWrap">
                                <input className="subtitles_word_editInput subtitles_word" 
                                    autoFocus={true} 
                                    value={editValue} 
                                    onChange={(e)=>{setEditValue(e.target.value)}} 
                                    onBlur={()=>{
                                        setEditIndex(-1);
                                        onWordSave(segmentIndex, wordIndex, editValue.trim());
                                }}
                                ></input> 
                                <span className="subtitles_word_editSize subtitles_word">{editValue}</span>
                            </span> : 
                            <span key={`word-${wordCount}`} data-id={wordCount} 
                                className={getSubtitleWordStyle(word, lastWord, wordCount)} 
                                onClick={()=>onWordClick(word.start)}
                                onMouseEnter={(e)=>{
                                    onHoverWord({word, element:e.target as HTMLElement});
                                    const el = e.target as HTMLElement;
                                    const wordId = Number(el.dataset['id']);
                                    const wordSelections = selections.filter(s => s.startId <= wordId && s.endId >= wordId);
                                    const commentSel = wordSelections.find(s => s.type === 'comment');
                                    const folderSel = wordSelections.find(s => s.type === 'folder');
                                    if (commentSel || folderSel) {
                                        const rect = el.getBoundingClientRect();
                                        const text = commentSel ? commentSel.comment : `📁 ${folderSel.folderName}`;
                                        setHoverTooltip({ x: rect.left + rect.width / 2, y: rect.top, text });
                                    } else {
                                        setHoverTooltip(null);
                                    }
                                }}
                                onMouseLeave={()=>{
                                    onHoverWord(null);
                                    setHoverTooltip(null);
                                }}
                            >
                            {word.word + ' '}
                        </span>;
                    lastWord = word;
                    return result;
                })}
            </div>
        })}
        </div>
    {showContextMenu && <div ref={contextMenuRef} className="subtitlesContextMenu_position" style={{top: showContextMenu.y, left: showContextMenu.x}}>
        <ContextMenu key={showContextMenu.y + '-'+showContextMenu.x} 
            position={{top: showContextMenu.y, left: showContextMenu.x}} 
            boundingElement={ref.current.parentElement.parentElement.parentElement}
            onComment={()=>{
                const selection = document.getSelection();
                if (!selection.rangeCount) return;
                const selectedRanges = getRangesInRange(selectionRange).filter(it=>it.type == 'comment');
                setShowEditComment({y: showContextMenu.y, x: showContextMenu.x, comment: selectedRanges[0]});
                setShowContextMenu(null);
            }}
            onSendFolder={()=>{
                onSelectionAction('sendFolder', getSelectedRange());
                setShowContextMenu(null);
            }}
            onSendDesktop={()=>{
                handleSendToDesktop();
                setShowContextMenu(null);
            }}
            onMark={(type)=>{
                const startNode = selectionRange.startContainer.parentNode as HTMLElement;
                const endNode = selectionRange.endContainer.parentNode as HTMLElement;
                const selectedRanges = getRangesInRange(selectionRange).filter(it=>['attention', 'redflag'].includes(it.type));
                if (selectedRanges[0]){
                    if (selectedRanges[0].type == type){
                        setSelections(last=>last.filter(it=>it != selectedRanges[0]));
                    } else {
                        setSelections(last=>last.map(it=>{
                            if (it != selectedRanges[0]){
                                return it;
                            } else {
                                return {...selectedRanges[0], type: type}
                            }
                        }));
                    }
                } else if (startNode?.dataset?.['id'] && endNode?.dataset?.['id']){
                    const newSelection: ISelectedRange = {
                        startId: Number(startNode.dataset['id']), 
                        endId: Number(endNode.dataset['id']), 
                        startOffset: selectionRange.startOffset, 
                        endOffset: selectionRange.endOffset,
                        type: type,
                    }; 
                    setSelections(last=>[...last, newSelection]);
                }
                setShowContextMenu(null);
            }}
            onEdit={()=>{
                const endNode = selectionRange.endContainer.parentNode as HTMLElement;
                if (endNode?.dataset?.['id']){
                    setEditIndex(Number(endNode?.dataset?.['id']));
                    setEditValue(endNode.textContent);
                    setShowContextMenu(null);
                }
            }}
        ></ContextMenu>
    </div>}
    {hoverTooltip && (
        <div className="subtitles_tooltip" style={{left: hoverTooltip.x, top: hoverTooltip.y}}>
            {hoverTooltip.text}
        </div>
    )}
    {showEditComment && <div ref={editCommentRef} className="subtitlesContextMenu_position" style={{
            top: showEditComment.y, 
            left: showEditComment.x
        }}>
        <CommentPopup
            position={{top: showEditComment.y, left: showEditComment.x}}
            onClose={() => setShowEditComment(null)}
            boundingElement={ref.current.parentElement.parentElement.parentElement}
            onDone={(text) => {
                setShowEditComment(null);

                if (showEditComment.comment){
                    if (text.trim() == ""){
                        setSelections(last=>last.filter(it=>it != showEditComment.comment));
                    } else {
                        setSelections(last=>last.map(it=>{
                            if (it != showEditComment.comment){
                                return it;
                            } else {
                                return {...showEditComment.comment, comment: text}
                            }
                        }));
                    }
                } else {
                    if (text.trim() == ""){
                        return;
                    }
                    const startNode = selectionRange.startContainer.parentNode as HTMLElement;
                    const endNode = selectionRange.endContainer.parentNode as HTMLElement;
                    if (startNode?.dataset?.['id'] && endNode?.dataset?.['id']){

                        setSelections(last=>[...last, {
                            startId: Number(startNode.dataset['id']), 
                            endId: Number(endNode.dataset['id']), 
                            startOffset: selectionRange.startOffset, 
                            endOffset: selectionRange.endOffset,
                            type: 'comment',
                            comment: text
                        }]);
                    }
                }
            }}
            initialText={showEditComment.comment?.comment || ''}
        ></CommentPopup>
    </div>}
    </div>
}